import { SelectChangeEvent } from "@mui/material";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetProgramTemplateByLobId } from "../../../../../api/services/programTemplate/programTemplateApis";
import { OptionsDataType } from "../../../../../components";
import { BILLING_DETAILS_SITE_CORE_VALUE } from "../../../../../helpers/constants";
import {
  getValueOrBlank,
  getValueOrBlankArray,
  isEmpty,
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { toggleLoader } from "../../../../../redux/slices/appState/appStateSlice";
import { CreateClientFormKey as ProgramTemplateFormKey } from "../../../modelsAndSchemas/Models";
import { BillingTypeFieldProps } from "./BillingTypeField";

export const useBillingTypeFieldHook = (props: BillingTypeFieldProps) => {
  const {
    formIdPrefix,
    currencyFormat,
    currencyId,
    defaultUserCurrency,
    currenciesList,
    forceUpdate,
    setShowWarningOnProgramBillingChange,
    templateIndex,
    setIsErrorFromProgramTemplate,
    isErrorFromProgramTemplate,
    lobId,
  } = props;

  const { control, setValue, getValues, watch } = useFormContext();
  const dispatch = useDispatch();
  const [lastBillingType, setLastBillingType] = useState("");
  const [lastProgramTemplate, setLastProgramTemplate] = useState("");
  const [programTemplateDropdownData, setProgramTemplateDropdownData] =
    useState<OptionsDataType[]>([]);

  const {
    commonLabels,
    fetchDropdownValues,
    fetchLabel,
    fetchValidationMessage,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const {
    mutateAsync: getProgramTemplateDropdownData,
    data: programTemplateList,
    isPending: isProgramTemplatePending,
  } = useGetProgramTemplateByLobId();

  const programTemplateIdKey = `${formIdPrefix}.${ProgramTemplateFormKey.programTemplateId}`;
  const programTemplateId = getValues(programTemplateIdKey);
  const billingTypeKey = `${formIdPrefix}.${ProgramTemplateFormKey.billingTypeId}`;
  const billingType = getValues(billingTypeKey);
  const billingSchedulesKey = `${formIdPrefix}.${ProgramTemplateFormKey.billingSchedules}`;
  const feePaymentTypeKey = `${formIdPrefix}.${ProgramTemplateFormKey.feePaymentType}`;
  const feeKey = `${formIdPrefix}.${ProgramTemplateFormKey.fee}`;
  const feeWithFormatKey = `${formIdPrefix}.${ProgramTemplateFormKey.feeWithFormat}`;

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isProgramTemplatePending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isProgramTemplatePending]);

  useEffect(() => {
    setProgramTemplateDropdownData([]);
    if (lobId) getProgramTemplateDropdownData(getValueOrBlank(lobId));
  }, [lobId]);

  useEffect(() => {
    if (isEmpty(lastProgramTemplate)) setLastProgramTemplate(programTemplateId);
  }, [watch(programTemplateIdKey)]);

  useEffect(() => {
    if (isEmpty(lastBillingType)) setLastBillingType(billingType);
  }, [watch(billingTypeKey)]);

  useEffect(() => {
    if (programTemplateList) {
      const isProgramTemplateValid = programTemplateList.some(
        (template) => template.id === programTemplateId,
      );
      if (!isProgramTemplateValid) {
        setValue(programTemplateIdKey, "");
      }
    }
    setProgramTemplateDropdownData(
      setApiDataToDropDownOption(getValueOrBlankArray(programTemplateList)),
    );
  }, [programTemplateList]);

  const clearMilestoneBasedValue = () => {
    setValue(`${formIdPrefix}.${ProgramTemplateFormKey.milestones}`, "");
    setValue(`${formIdPrefix}.${ProgramTemplateFormKey.activities}`, "");
    setValue(feePaymentTypeKey, "");
    setValue(billingSchedulesKey, []);
  };

  const clearScheduleBasedValue = () => {
    setValue(`${formIdPrefix}.${ProgramTemplateFormKey.schedulePeriod}`, "");
    setValue(`${formIdPrefix}.${ProgramTemplateFormKey.schedules}`, "");
    setValue(feePaymentTypeKey, "");
    setValue(billingSchedulesKey, []);
  };

  const clearActivityBasedValue = () => {
    setValue(`${formIdPrefix}.${ProgramTemplateFormKey.billingTriggerId}`, "");
  };

  const clearOnBillingTypeChange = () => {
    switch (billingType) {
      case BILLING_DETAILS_SITE_CORE_VALUE.billingTypeImmediateBilling:
        clearMilestoneBasedValue();
        clearScheduleBasedValue();
        clearActivityBasedValue();
        break;
      case BILLING_DETAILS_SITE_CORE_VALUE.billingTypeActivity:
        clearMilestoneBasedValue();
        clearScheduleBasedValue();
        break;
      case BILLING_DETAILS_SITE_CORE_VALUE.billingTypeMilestone:
        clearScheduleBasedValue();
        clearActivityBasedValue();
        break;
      case BILLING_DETAILS_SITE_CORE_VALUE.billingTypeSchedule:
        clearMilestoneBasedValue();
        clearActivityBasedValue();
        break;
      default:
        break;
    }
  };

  const checkAllowProgramTemplateOrBillingToChange = () => {
    const billingSchedules = getValueOrBlankArray(
      getValues(billingSchedulesKey),
    );
    return !isNotEmpty(billingSchedules);
  };

  const handleConfirmationStatusTrue = () => {
    clearOnBillingTypeChange();
  };

  const handleConfirmationStatusFalse = () => {
    if (isErrorFromProgramTemplate) {
      setValue(programTemplateIdKey, lastProgramTemplate);
    } else {
      setValue(billingTypeKey, lastBillingType);
    }
  };

  const handleProgramTemplateOrBillingTypeChange = (
    e: SelectChangeEvent<unknown>,
    isErrorFromProgramTemplate: boolean,
  ) => {
    setIsErrorFromProgramTemplate(isErrorFromProgramTemplate);
    let isPauseLastSelection = false;
    if (!checkAllowProgramTemplateOrBillingToChange()) {
      setShowWarningOnProgramBillingChange({
        showConfirmation: true,
        indexNumber: templateIndex,
      });
      isPauseLastSelection = true;
    } else {
      clearOnBillingTypeChange();
    }

    if (!isPauseLastSelection) {
      if (isErrorFromProgramTemplate) {
        const programTemplateId = e.target.value as string;
        setLastProgramTemplate(programTemplateId);
      } else {
        const billingType = e.target.value as string;
        setLastBillingType(billingType);
      }
    }
    forceUpdate();
  };

  return {
    control,
    setValue,
    getValues,
    programTemplateDropdownData,
    commonLabels,
    fetchDropdownValues,
    fetchLabel,
    fetchValidationMessage,
    commonARIALabel,
    programTemplateIdKey,
    billingTypeKey,
    feeWithFormatKey,
    feeKey,
    billingType,
    handleConfirmationStatusTrue,
    handleConfirmationStatusFalse,
    handleProgramTemplateOrBillingTypeChange,
    currencyFormat,
    currencyId,
    defaultUserCurrency,
    currenciesList,
    formIdPrefix,
    forceUpdate,
  };
};
